import {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useParams} from 'wouter';

import {FrontendMedia, FrontendSupportMessage} from '@shared/models/api_models';
import {SupportMessageId, SupportMessageThreadId} from '@shared/models/mavismart_ids';

import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';

import {Loader} from '@mavismart_frontend/components/loader';
import {apiCall} from '@mavismart_frontend/lib/api';
import {HEADER_HEIGHT} from '@mavismart_frontend/lib/constants';
import {getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

export const SupportMessagePage: FC = () => {
  const {threadId = '' as SupportMessageThreadId, messageId = '' as SupportMessageId} = useParams<{
    threadId: SupportMessageThreadId;
    messageId: SupportMessageId;
  }>();
  const account = useAccount();
  const locale = getUserLocale(account);
  const [supportMessage, setSupportMessage] = useState<FrontendSupportMessage | undefined>(
    undefined
  );
  const [attachments, setAttachments] = useState<FrontendMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSupportMessage(undefined);
    setAttachments([]);

    apiCall('POST /support-get-message', {threadId, messageId})
      .then(async ({supportMessage}) => {
        if (!isMounted) {
          return;
        }
        setSupportMessage(supportMessage);

        if (supportMessage.mediaIds.length === 0) {
          return;
        }

        setIsLoadingAttachments(true);
        const medias = await Promise.all(
          supportMessage.mediaIds.map(async mediaId => {
            try {
              const res = await apiCall('POST /get-media', {mediaId});
              return res.media;
            } catch (error) {
              notifyError(error);
              return undefined;
            }
          })
        );

        if (isMounted) {
          setAttachments(medias.filter((m): m is FrontendMedia => m !== undefined));
        }
      })
      .catch(notifyError)
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
          setIsLoadingAttachments(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [messageId, threadId]);

  const formattedDate = useMemo(() => {
    if (!supportMessage) {
      return '';
    }
    try {
      return new Date(supportMessage.ts).toLocaleString(locale);
    } catch {
      return '';
    }
  }, [supportMessage, locale]);

  if (isLoading) {
    return (
      <PageWrapper>
        <Loader />
      </PageWrapper>
    );
  }

  if (!supportMessage) {
    return <PageWrapper>Support message introuvable.</PageWrapper>;
  }

  return (
    <PageWrapper>
      <Card $gap="lg">
        <Title>Message SAV</Title>
        <Metadata>
          <MetadataItem>
            <Label>Envoyé par</Label>
            <Value>{supportMessage.email}</Value>
          </MetadataItem>
          <MetadataItem>
            <Label>Porte</Label>
            <Value>{supportMessage.doorId}</Value>
          </MetadataItem>
          <MetadataItem>
            <Label>Message (ts)</Label>
            <Value>{supportMessage.doorMessageTs}</Value>
          </MetadataItem>
          <MetadataItem>
            <Label>Référence</Label>
            <Value>
              {supportMessage.threadId} / {supportMessage.id}
            </Value>
          </MetadataItem>
          {formattedDate !== '' && (
            <MetadataItem>
              <Label>Date</Label>
              <Value>{formattedDate}</Value>
            </MetadataItem>
          )}
        </Metadata>
        <ContentBox>{supportMessage.content}</ContentBox>
      </Card>

      <Card $gap="md">
        <Title>Pièces jointes</Title>
        {isLoadingAttachments ? (
          <Loader />
        ) : attachments.length === 0 ? (
          <Muted>Aucune pièce jointe</Muted>
        ) : (
          <Flex $column $gap="m">
            {attachments.map(media => (
              <Attachment key={media.id}>
                <div>
                  <strong>{media.fileName}</strong> ({media.contentType})
                </div>
                <AttachmentLink href={media.url} target="_blank" rel="noreferrer">
                  Ouvrir
                </AttachmentLink>
              </Attachment>
            ))}
          </Flex>
        )}
      </Card>
    </PageWrapper>
  );
};

const PageWrapper = styled(Flex).attrs({$column: true})`
  padding: 24px;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  margin-top: calc(${HEADER_HEIGHT}px + env(safe-area-inset-top));
  height: calc(100% - ${HEADER_HEIGHT}px - env(safe-area-inset-top));
`;

const Card = styled(Flex).attrs({$column: true})<{$gap?: 'md' | 'lg'}>`
  background: #ffffff;
  border: 1px solid #e3e3e3;
  border-radius: 12px;
  padding: 20px;
  gap: ${({$gap}) => ($gap === 'lg' ? '16px' : '12px')};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 700;
`;

const Metadata = styled(Flex).attrs({$wrap: true})`
  gap: 12px 24px;
`;

const MetadataItem = styled(Flex).attrs({$column: true})`
  gap: 4px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #7c7c7c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-size: 15px;
  color: #1b1b1b;
`;

const ContentBox = styled.div`
  padding: 16px;
  background: #f8f8f8;
  border-radius: 10px;
  border: 1px solid #ededed;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const Muted = styled.div`
  color: #7c7c7c;
`;

const Attachment = styled(Flex)`
  padding: 12px 14px;
  border: 1px solid #ededed;
  border-radius: 10px;
  background: #fafafa;
  gap: 10px;
`;

const AttachmentLink = styled.a`
  color: #ed1a3b;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
